// ---------------------------------------------------------------------------
// Guidebook — Public Guest Types
// ---------------------------------------------------------------------------

export interface GuidebookProperty {
  name: string
  address: string
  region_description: string
  wifi_name: string
  wifi_password: string
  parking_instruction: string
  trash_instruction: string
  supermarket_hint: string
  lat: number | null
  lng: number | null
  door_code: string
  door_code_status: "visible" | "pending" | "checkout" | "disabled" | "not_configured"
  door_code_available_at?: string
  door_code_available_date?: string
  checkin_time: string
  cover_image_url?: string
}

export type SensitiveFieldKey = "wifi_password" | "door_code" | "address_exact"
export type DoorCodeVisibilityHint = "available_now" | "available_on_arrival" | "locked"
export type GuidebookTemplate = "classic" | "concierge" | "reserve"
export type HouseVoice = "house_standard" | "attentive_host" | "english_butler" | "warm_local" | "minimalist"

export interface GuidebookPlaceCopy {
  name: string
  short?: string
  why?: string
  tip?: string
  rainAlternative?: string
  bestFor?: string
}

export interface PlaceOpeningHours {
  /** Human-readable summary, e.g. "Tue–Sat 11:00–22:00" */
  summary?: string
  /** Days of week the place is open (0=Sun … 6=Sat). Absent = unknown / always open. */
  openDays?: number[]
  /** Earliest open hour (0–23). Absent = unknown. */
  opensAt?: number
  /** Latest open hour (0–23). Absent = unknown. */
  closesAt?: number
  /** Where this data came from: "research", "rules", "host". */
  source?: string
}

export interface GuidebookPlace {
  id: string
  area?: string
  type?: string
  seasonality?: string
  priceRange?: string
  coordinates?: { lat: number; lng: number }
  familyFriendly?: boolean
  suitableFor?: string[]
  tags?: string[]
  website?: string
  openingHours?: PlaceOpeningHours
  en?: GuidebookPlaceCopy
}

export interface GuidebookHouseRules {
  quietHours?: { enabled: boolean; startTime?: string; endTime?: string; strictness?: string; surfaces?: { guidebook?: boolean; display?: boolean }; delivery?: string }
  parties?: { enabled: boolean; value?: string; notes?: string; strictness?: string; surfaces?: { guidebook?: boolean; display?: boolean }; delivery?: string }
  smoking?: { enabled: boolean; value?: string; notes?: string; strictness?: string; surfaces?: { guidebook?: boolean; display?: boolean }; delivery?: string }
  pets?: { enabled: boolean; value?: string; notes?: string; strictness?: string; surfaces?: { guidebook?: boolean; display?: boolean }; delivery?: string }
  visitors?: { enabled: boolean; value?: string; notes?: string; strictness?: string; surfaces?: { guidebook?: boolean; display?: boolean }; delivery?: string }
  checkout?: { enabled: boolean; checkoutTime?: string; checklist?: string[] }
  waste?: { enabled: boolean; binLocation?: string; recyclingTypes?: string[]; notes?: string }
  safety?: { enabled: boolean; enabledRules?: string[]; customRules?: string[] }
  parking?: { instruction?: string; notes?: string }
  outdoor?: { rules?: string[]; notes?: string }
  custom?: { rules?: string[] }
}

export interface GuidebookWeather {
  temperature_c: number | null
  apparent_temperature_c: number | null
  weather_code: number | null
  weather_label: string | null
  wind_speed_ms: number | null
  wind_gusts_ms: number | null
  wind_direction_deg: number | null
  humidity_pct: number | null
  cloud_cover_pct: number | null
  precipitation_mm: number | null
  is_day: boolean | null
  sunrise: string | null
  sunset: string | null
  temp_max_c: number | null
  temp_min_c: number | null
  timezone: string | null
  fetched_at: string | null
}

export interface GuidebookWelcome {
  headline?: string
  message?: string
  hero_message?: string
  welcome_headline?: string
  welcome_message?: string
}

export interface GuidebookStrategy {
  summary: string
  stay_profile: string
  party_profile: string
  nights: number
  adults: number
  children: number
  check_in: string
  check_out: string
}

export interface GuidebookAccess {
  mode: "pms" | "ical"
  sensitive_locked: boolean
  pin_required: boolean
}

export type HighlightSlot =
  | "beach_now"
  | "morning_coffee"
  | "lunch"
  | "dinner"
  | "activity_now"
  | "tomorrow_plan"

export interface GuidebookHighlight {
  slot: HighlightSlot
  place_id: string
  label: string
  motivation: string
  emoji: string
}

export interface TvSlideConfig {
  type: "welcome" | "weather" | "wifi" | "places" | "rules" | "photo"
  enabled: boolean
  duration: number
  order: number
}

export interface TvState {
  booking_phase: "welcome" | "during_stay" | "last_evening" | "checkout" | "between_bookings"
  guest_name?: string | null
  check_in?: string | null
  check_out?: string | null
}

export interface NightModeConfig {
  enabled: boolean
  start_hour: number
  end_hour: number
}

export interface GuidebookBasePayload {
  ok: boolean
  error?: string
  template?: GuidebookTemplate
  house_voice?: HouseVoice
  property: GuidebookProperty
  places: GuidebookPlace[]
  houseRules: GuidebookHouseRules
  welcome: GuidebookWelcome | null
  strategy: GuidebookStrategy | null
  weather: GuidebookWeather | null
  highlights?: GuidebookHighlight[]
  tv_slide_config?: TvSlideConfig[] | null
  tv_state?: TvState | null
  night_mode?: NightModeConfig | null
  intelligence_gate?: { guidebook_grace_ms: number } | null
}

export interface PublicGuidebookPayload extends GuidebookBasePayload {
  strategy: null
  access: GuidebookAccess
  authMode: "pms" | "ical"
  requiresPinForSensitive: boolean
  sensitiveFields: SensitiveFieldKey[]
  doorCodeVisibility: DoorCodeVisibilityHint
  doorCodePolicyText?: string
  is_public: true
}

export interface PrivateGuidebookPayload extends GuidebookBasePayload {
  access?: GuidebookAccess
  authMode?: "pms" | "ical"
  requiresPinForSensitive?: boolean
  sensitiveFields?: SensitiveFieldKey[]
  doorCodeVisibility?: DoorCodeVisibilityHint
  doorCodePolicyText?: string
  is_public?: false
}

export type GuidebookData = PublicGuidebookPayload | PrivateGuidebookPayload

// ---------------------------------------------------------------------------
// Place category mapping
// ---------------------------------------------------------------------------

export type PlaceCategory =
  | "Eat & drink"
  | "Coffee & bakery"
  | "Beach"
  | "Activities"
  | "Shopping"
  | "Other"

export const PLACE_CATEGORIES: PlaceCategory[] = [
  "Eat & drink",
  "Coffee & bakery",
  "Beach",
  "Activities",
  "Shopping",
  "Other",
]

const CATEGORY_MAP: Record<string, PlaceCategory> = {
  restaurant: "Eat & drink",
  bar: "Eat & drink",
  bistro: "Eat & drink",
  pub: "Eat & drink",
  wine_bar: "Eat & drink",
  cafe: "Coffee & bakery",
  bakery: "Coffee & bakery",
  ice_cream: "Coffee & bakery",
  coffee: "Coffee & bakery",
  beach: "Beach",
  attraction: "Activities",
  park: "Activities",
  hiking: "Activities",
  sports: "Activities",
  museum: "Activities",
  gallery: "Activities",
  nature: "Activities",
  spa: "Activities",
  golf: "Activities",
  shop: "Shopping",
  market: "Shopping",
  boutique: "Shopping",
  supermarket: "Shopping",
}

export function getCategoryForType(type: string): PlaceCategory {
  return CATEGORY_MAP[type?.toLowerCase()] || "Other"
}
